import type { AnalyticsWindow, DutyAnalyticsRange } from '@pams/shared';

/**
 * THE CENTRAL QUERY-KEY FACTORY (CLAUDE.md §8).
 *
 * No ad-hoc string keys anywhere in the app. The reason is invalidation: a mutation has
 * to be able to say "everything under this entity is now stale", and it can only do
 * that if the keys are hierarchical and defined in one place. Scattered string literals
 * drift by a character, and the symptom is a list that silently fails to refresh.
 *
 * The shape is the standard prefix hierarchy — `all` → `lists()` → `list(params)` —
 * so `invalidateQueries({ queryKey: queryKeys.officers.all })` catches every descendant.
 */
export const queryKeys = {
  system: {
    all: ['system'] as const,
    health: () => [...queryKeys.system.all, 'health'] as const,
    ready: () => [...queryKeys.system.all, 'ready'] as const,
    version: () => [...queryKeys.system.all, 'version'] as const,
  },
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },
  orgUnits: {
    all: ['orgUnits'] as const,
    trees: () => [...queryKeys.orgUnits.all, 'tree'] as const,
    /**
     * `'scope'` when no root is given: the caller's own scope root is the implicit one.
     *
     * [amended P1a.2] `params` was added because the org screen now issues SEVERAL tree
     * queries — `includeInactive` on and off, and page 1..n — which would otherwise
     * collide on one key and serve each other's data. Optional, so `tree()` still works
     * and `OrgUnitPicker` needs no edit.
     */
    tree: (rootId?: string, params?: object) =>
      [...queryKeys.orgUnits.trees(), rootId ?? 'scope', ...(params ? [params] : [])] as const,
    /**
     * [amended P1b] `GET /org-units/counts`. Under `orgUnits` and not `officers`
     * because that is the endpoint it calls — and because the officer flows already
     * invalidate `orgUnits.all` for the tree payload, so this is covered with no new
     * wiring to forget.
     */
    counts: (params: object) => [...queryKeys.orgUnits.all, 'counts', params] as const,
    lists: () => [...queryKeys.orgUnits.all, 'list'] as const,
    list: (params: object) => [...queryKeys.orgUnits.lists(), params] as const,
    details: () => [...queryKeys.orgUnits.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orgUnits.details(), id] as const,
    /**
     * [C1b §4] The UNSCOPED unit search behind the associated-units picker.
     *
     * ⚠️ A SIBLING OF `list`, NOT A CHILD. It answers a different question over a different
     * endpoint with a different scope rule, and nesting it under `lists()` would put it in
     * the blast radius of every list invalidation for no reason.
     */
    searches: () => [...queryKeys.orgUnits.all, 'search'] as const,
    search: (term: string) => [...queryKeys.orgUnits.searches(), term] as const,
  },
  officers: {
    all: ['officers'] as const,
    lists: () => [...queryKeys.officers.all, 'list'] as const,
    list: (params: object) => [...queryKeys.officers.lists(), params] as const,
    details: () => [...queryKeys.officers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.officers.details(), id] as const,
    postings: (id: string) => [...queryKeys.officers.detail(id), 'postings'] as const,
  },
  /**
   * ⚠️ [P4] A TOP-LEVEL NAMESPACE, DELIBERATELY *NOT* UNDER `arrangements`.
   *
   * `arrangements.all` is `['arrangements']` and is a PREFIX of `detail(id)`. P3b.3's
   * data-loss bug was exactly that: a mutation invalidated at `.all`, the record being
   * edited refetched, and a hydration effect wrote the server's copy over what was being
   * typed. The fill screen takes up to TWELVE writes in ninety seconds — twelve chances to
   * fire the same bug — so its key is put where no existing invalidation can reach it, and
   * the invariant becomes MECHANICAL rather than remembered.
   *
   * ⚠️ NOTHING IN THE APP MAY INVALIDATE `fill.all` OR `fill.sheets()`. Both are prefixes of
   * a live sheet. `web/src/lib/fillKeyIsolation.test.ts` reads the source and fails if one
   * ever appears — a runtime test can only cover the paths a test walks.
   */
  fill: {
    all: ['fill'] as const,
    sheets: () => [...queryKeys.fill.all, 'sheet'] as const,
    /**
     * THE RECORD BEING EDITED. Written with `setQueryData`; never invalidated.
     *
     * ⚠️ [D4.5 §3e] THE DIVISION IS APPENDED ONLY WHEN THERE IS ONE, AND THAT IS NOT TIDINESS.
     * §3e lets a super admin open one division's share of a duty two divisions were asked to
     * fill, which is a genuinely different sheet and must not share a cache entry with the
     * whole one. But appending `undefined` unconditionally would move EVERY existing key by
     * one element — and `lib/fillKeyIsolation.test.ts` plus every `setQueryData` patch on the
     * hot path address these keys exactly. Absent division, absent element.
     */
    sheet: (dutyId: string, division?: string) =>
      division
        ? ([...queryKeys.fill.sheets(), dutyId, division] as const)
        : ([...queryKeys.fill.sheets(), dutyId] as const),
    /**
     * ⚠️ A SIBLING OF `sheet`, NOT A CHILD — the opposite of `arrangements.deployments`, and
     * for the opposite reason. Those nest so a parent invalidation catches them. This one
     * must NOT be catchable from the sheet: it is read once, staged in local state, and
     * re-reading it mid-review would replace the operator's adjustments with last week's.
     */
    copySources: () => [...queryKeys.fill.all, 'copy-source'] as const,
    copySource: (dutyId: string) => [...queryKeys.fill.copySources(), dutyId] as const,
    /**
     * ⚠️ [PRE-P6.2] THE DUTY'S CHANGE HISTORY, a SIBLING of `sheet` for the same reason
     * `candidates` is. It must not be catchable from a sheet invalidation, and the sheet must
     * not be catchable from it — a log nobody edits has no business refetching on a burst of
     * writes, and `fillKeyIsolation.test.ts` is what holds both halves.
     */
    history: (dutyId: string) => [...queryKeys.fill.all, 'history', dutyId] as const,
    /**
     * The search sheet. Keyed on the slot AND the query, so the same question asked twice
     * while the sheet is open is answered from cache — which is most of what keeps the
     * common case at Enter alone.
     *
     * ⚠️ [amended C1a §3c] A SIBLING OF `sheet`, WHICH IS WHAT `history` ABOVE ALREADY
     * CLAIMED IT WAS AND IT WAS NOT.
     *
     * It was built on `fill.sheet(dutyId)`, so the sheet key was a strict PREFIX of every
     * candidate list for that duty — and of the divisioned sheet as well. Nothing invalidates
     * it today, so nothing was broken; what was broken is that the comment one field up
     * described a property the code did not have, and `fillKeyIsolation.test.ts` guarded
     * `fill.all` and `fill.sheets()` but not `fill.sheet(...)`. A future
     * `invalidateQueries(fill.sheet(id))` would have swept the candidate cache mid-sitting
     * and passed every guard in the repository.
     *
     * Both halves are closed: the key is genuinely a sibling now, and the guard covers
     * `fill.sheet` too.
     */
    candidates: (dutyId: string, slotRef: string, search: string) =>
      [...queryKeys.fill.all, 'candidates', dutyId, slotRef, search] as const,
    /**
     * ⚠️ [C3c §4b] THE 90-DAY DUTY COUNTS, A SIBLING OF BOTH `sheet` AND `candidates`.
     *
     * Not a child of either, for `candidates`' own recorded reason: the sheet must not be able
     * to catch this from an invalidation and this must not be able to catch the sheet, and
     * `fillKeyIsolation.test.ts` is what holds both halves. [C1a §3c] found `candidates` itself
     * built on `fill.sheet(dutyId)` — a strict prefix — so this is the second instance of a
     * mistake that has already been made once here.
     *
     * ⚠️ THE OFFICER IDS ARE IN THE KEY, WHICH IS WHAT MAKES §4b's "THE QUEUE RESETS" TRUE
     * WITHOUT ANY CANCELLATION. When the search term changes, the list changes, so every batch
     * key changes — and requests still in flight for the old term resolve into cache entries
     * NOTHING RENDERS. There is no tail of results landing in a list that has moved on, because
     * a landed result has nowhere to land.
     */
    dutyCounts: (dutyId: string, slotRef: string, officerIds: string) =>
      [...queryKeys.fill.all, 'duty-counts', dutyId, slotRef, officerIds] as const,
    /**
     * ⚠️ [C5 §11d] ONE OFFICER'S THREE MONTHS, KEYED ON THE OFFICER — which IS §11d's cache.
     *
     * A SIBLING of `dutyCounts`, never a child and never the same key: the two read the same
     * ROUTE at different grains and store different shapes, and [PRE-P6 §1b]'s measured rule is
     * that a query key belongs to exactly one thing. Keying on the officer is what makes a
     * second hover free — TanStack answers from the entry and fires nothing.
     *
     * ⚠️ AND IT CARRIES THE SLOT, because the ANSWER depends on it: the endpoint narrows to the
     * `addressedUnitIds` of the slot's own addressee, so the same officer on two slots is two
     * questions with two permissible answers.
     */
    dutyMonths: (dutyId: string, slotRef: string, officerId: string) =>
      [...queryKeys.fill.all, 'duty-months', dutyId, slotRef, officerId] as const,
    /**
     * [C1b §3] The split modal's numbers.
     *
     * ⚠️ A SIBLING OF `sheet`, NEVER A CHILD. `fillKeyIsolation.test.ts` fails if anything
     * invalidates at or above a live sheet, and nesting this under `sheets()` would put the
     * sheet in the blast radius of a modal opening. `candidates` and `copySource` are here
     * for the same reason.
     */
    delegations: () => [...queryKeys.fill.all, 'delegation'] as const,
    delegation: (dutyId: string, lineKey: string, allocKey: string) =>
      [...queryKeys.fill.delegations(), dutyId, lineKey, allocKey] as const,
    /**
     * [C6a §4] The units a division may hand a command position to — `GET
     * /duties/:id/command-seats/options`, one role asked of one division.
     *
     * ⚠️ A SIBLING OF `sheet`, NEVER A CHILD — `delegation`'s rule and its reason, one row up. The
     * sheet's command families are written back with `setQueryData` after every seat write; nothing
     * here may be reachable from that write, and it must not be able to reach the sheet.
     */
    commandSeatOptions: () => [...queryKeys.fill.all, 'command-seats'] as const,
    commandSeats: (dutyId: string, commandKey: string, divisionOrgUnitId: string) =>
      [...queryKeys.fill.commandSeatOptions(), dutyId, commandKey, divisionOrgUnitId] as const,
  },
  /**
   * [P6a] The scoped dashboard. ONE entry holds the WHOLE view — the resolved layout and
   * every tile's data — because that is what one request returns.
   *
   * ⚠️ THERE IS DELIBERATELY NO `dashboard.tile(id)`. A per-tile Retry writes its answer back
   * into this entry with `setQueryData` ([P4]'s rule: it neither refetches nor marks stale, so
   * there is no scheduled refetch for a local write to race). A second entry holding one tile
   * would be a SECOND SOURCE OF TRUTH for a number already on screen — and the two hooks would
   * then be one refactor away from sharing a key with different shapes, which is [PRE-P6] §1b's
   * crash.
   *
   * ⚠️ [amended P6b] AND ONE THING NOW INVALIDATES IT: SAVING A LAYOUT. This comment said
   * "nothing" and shipped true for one release; a configurator save changes what a tile is
   * CALLED and which tiles exist, so a super admin editing their own combination would
   * otherwise sit in front of a stale dashboard until the cache aged out. The prefix is the
   * right breadth here — an override changes every `view(params)` and every `rows(...)` at
   * once — and [P3b.3]'s hazard does not apply, because that was invalidating a prefix of a
   * record BEING EDITED and a layout save is a single deliberate act, not twelve writes in
   * ninety seconds.
   */
  dashboard: {
    all: ['dashboard'] as const,
    views: () => [...queryKeys.dashboard.all, 'view'] as const,
    view: (params: object) => [...queryKeys.dashboard.views(), params] as const,
    /**
     * ⚠️ A SIBLING OF `views()`, NEVER UNDER IT. The rows payload is a different SHAPE from a
     * dashboard view, and `policies.history`' recorded reason applies verbatim: two shapes
     * under one prefix is one invalidation away from a hook reading the other one's data.
     */
    rows: (tileId: string, params: object) => [...queryKeys.dashboard.all, 'rows', tileId, params] as const,
  },
  /**
   * [P6b] The configurator.
   *
   * ⚠️ A SIBLING OF `dashboard`, NEVER UNDER IT. `['dashboard','layouts']` would be a PREFIX
   * CHILD of `dashboard.all`, so the invalidation a save fires would sweep the configurator's
   * own rows out from under the screen that just wrote them — and two SHAPES under one prefix
   * is `policies.history`'s recorded reason for the same split.
   */
  dashboardLayouts: {
    all: ['dashboardLayouts'] as const,
    list: () => [...queryKeys.dashboardLayouts.all, 'list'] as const,
    config: (accessRole: string, orgUnitType: string) =>
      [...queryKeys.dashboardLayouts.all, 'config', accessRole, orgUnitType] as const,
  },
  /** [P4] The nodal's landing list. Rows are addressed SHARES, not orders. */
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [C1g §1d] THE NOTIFICATION BELL.
   *
   *  ⚠️ A SIBLING OF `inbox`, NEVER UNDER IT — AND THE REASON IS AN INVALIDATION, NOT TIDINESS.
   *
   *  `pages/DutyFill` flushes `inbox.all` on unmount, so a bell nested there would be swept by
   *  a naming burst on a screen that is not the bell's. That is right by accident and wrong by
   *  design: the bell must refresh after a fill, but because the WORK changed, which is a
   *  decision the fill screen states explicitly rather than one it makes by prefix. The
   *  dashboard/`dashboardLayouts` split records exactly this argument.
   *
   *  ⚠️ ONE KEY, ONE HOOK TYPE — `pwa`'s measured defect ([PRE-P6 §1b]). Nothing reads this
   *  with `useInfiniteQuery`; the payload is a bounded object, not a page.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
    /**
     * ⚠️ [P7B §6b] THE NOTICES — "an order you were on was taken back to draft". A SIBLING of
     * `list()`, never under it and never the same key: the bell answers "what work is LEFT"
     * (derived) and a notice "what was SENT" (stored), and [C3c.1 §6d] refused unifying them. Two
     * shapes under one key is [PRE-P6 §1b]'s crash, so each has its own entry and its own hook call.
     */
    notices: () => [...queryKeys.notifications.all, 'notices'] as const,
  },
  inbox: {
    all: ['inbox'] as const,
    lists: () => [...queryKeys.inbox.all, 'list'] as const,
    list: (params: object) => [...queryKeys.inbox.lists(), params] as const,
  },
  places: {
    all: ['places'] as const,
    lists: () => [...queryKeys.places.all, 'list'] as const,
    list: (params: object) => [...queryKeys.places.lists(), params] as const,
    details: () => [...queryKeys.places.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.places.details(), id] as const,
    /**
     * The near-duplicate probe. Keyed on the typed name, so the same question asked
     * twice while a dialog is open is answered from cache rather than re-asked.
     */
    suggestions: (name: string) => [...queryKeys.places.all, 'suggestions', name] as const,
  },
  ranks: {
    all: ['ranks'] as const,
    lists: () => [...queryKeys.ranks.all, 'list'] as const,
    list: (params: object) => [...queryKeys.ranks.lists(), params] as const,
    options: () => [...queryKeys.ranks.all, 'options'] as const,
  },
  postTypes: {
    all: ['postTypes'] as const,
    lists: () => [...queryKeys.postTypes.all, 'list'] as const,
    list: (params: object) => [...queryKeys.postTypes.lists(), params] as const,
    details: () => [...queryKeys.postTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.postTypes.details(), id] as const,
    /** The bounded whole-master read a picker needs. Mirrors `ranks.options()`. */
    options: () => [...queryKeys.postTypes.all, 'options'] as const,
  },
  vehicleTypes: {
    all: ['vehicleTypes'] as const,
    lists: () => [...queryKeys.vehicleTypes.all, 'list'] as const,
    list: (params: object) => [...queryKeys.vehicleTypes.lists(), params] as const,
    details: () => [...queryKeys.vehicleTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.vehicleTypes.details(), id] as const,
    options: () => [...queryKeys.vehicleTypes.all, 'options'] as const,
  },
  /** [P3b.1] Round-the-clock standing orders — the GENERATORS, not what they emit. */
  standingOrders: {
    all: ['standing-orders'] as const,
    lists: () => [...queryKeys.standingOrders.all, 'list'] as const,
    list: (params: object) => [...queryKeys.standingOrders.lists(), params] as const,
    details: () => [...queryKeys.standingOrders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.standingOrders.details(), id] as const,
    /**
     * ⚠️ NESTED UNDER `detail(id)`, so saving an order invalidates what it says it will
     * create with no extra wiring to forget — the same argument
     * `arrangements.publishPreview` already makes.
     */
    generationPreview: (id: string) => [...queryKeys.standingOrders.detail(id), 'generation-preview'] as const,
    editImpact: (id: string) => [...queryKeys.standingOrders.detail(id), 'edit-impact'] as const,
  },

  arrangements: {
    all: ['arrangements'] as const,
    lists: () => [...queryKeys.arrangements.all, 'list'] as const,
    list: (params: object) => [...queryKeys.arrangements.lists(), params] as const,
    details: () => [...queryKeys.arrangements.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.arrangements.details(), id] as const,
    /**
     * ⚠️ NESTED UNDER `detail(id)`, on the same argument as `policies.versions` — so
     * publishing an arrangement invalidates the duties it just generated with no extra
     * wiring to forget. Duties hanging off `all` would survive a publish and the detail
     * panel would keep rendering the empty list it had before.
     */
    deployments: (id: string, params?: object) =>
      [...queryKeys.arrangements.detail(id), 'deployment', ...(params ? [params] : [])] as const,
    /**
     * ⚠️ [C1e §2] NESTED UNDER `detail(id)` FOR `deployments`' REASON, VERBATIM: naming an
     * officer invalidates the detail, and the preview of the same order must go with it or a
     * modal opened afterwards renders the roster from before the name.
     */
    preview: (id: string) => [...queryKeys.arrangements.detail(id), 'preview'] as const,
    /** What publishing would create, asked before it happens. */
    publishPreview: (id: string) => [...queryKeys.arrangements.detail(id), 'publish-preview'] as const,
    /**
     * [D4.4 §5] What taking an order back to draft would destroy, asked before the click.
     *
     * ⚠️ NESTED UNDER `detail(id)` for `deployments`' reason: un-publishing changes the
     * record, and an impact that survived it would state counts for duties that no longer
     * exist. Everything on this page invalidates through `arrangements.all` already.
     */
    unpublishImpact: (id: string) => [...queryKeys.arrangements.detail(id), 'unpublish-impact'] as const,
    /**
     * [P6c §6] The printed order sheet — the record and its full named roster, in one read.
     *
     * ⚠️ NESTED UNDER `detail(id)` for `deployments`' reason, and here it matters more:
     * naming an officer must not leave a printed sheet showing the roster from before. Every
     * fill writer already flushes `arrangements.detail(id)`, so this invalidates with it and
     * there is no second thing to remember.
     */
    orderSheet: (id: string) => [...queryKeys.arrangements.detail(id), 'order-sheet'] as const,
    /**
     * ⚠️ [C2b §3] A DESCENDANT OF `detail(id)`, WHICH IS WHAT MAKES THE INVALIDATION FREE.
     * Nothing on the detail page invalidates `arrangements.all` ([P3b.3]'s prefix defect), and
     * an upload has to refresh only this list — so it invalidates its own key and nothing else.
     */
    attachments: (id: string) => [...queryKeys.arrangements.detail(id), 'attachments'] as const,
    /**
     * [P7B §6b] What taking a RUNNING order back to draft would destroy — a `master_superadmin`
     * override. Nested under `detail(id)` for `unpublishImpact`'s reason: the revert changes the
     * record, and an impact that survived it would state counts for duties that are gone.
     */
    revertImpact: (id: string) => [...queryKeys.arrangements.detail(id), 'revert-impact'] as const,
  },
  policies: {
    all: ['policies'] as const,
    lists: () => [...queryKeys.policies.all, 'list'] as const,
    list: (params: object) => [...queryKeys.policies.lists(), params] as const,
    details: () => [...queryKeys.policies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.policies.details(), id] as const,
    /**
     * ⚠️ NESTED UNDER `detail(policyId)`, not under `all` directly — so invalidating one
     * policy catches its versions too. A version list hanging off `all` would survive a
     * publish and the builder would keep rendering the version it just froze.
     */
    versions: (policyId: string) => [...queryKeys.policies.detail(policyId), 'version'] as const,
    version: (policyId: string, versionId: string) => [...queryKeys.policies.versions(policyId), versionId] as const,
    /**
     * ⚠️ [PRE-P6 §3b] THE LIST OF VERSIONS, WHICH IS A DIFFERENT SHAPE FROM ANY ONE OF THEM.
     *
     * A SIBLING of each `version(id)` rather than the same key as `versions(policyId)` —
     * a `Paginated<PolicyVersionDto>` and a `PolicyVersionDto` must never share a cache
     * entry, whatever they are called. §1b of this pass is what happens when two hooks with
     * different data shapes agree on a key: `pwa`'s Home wrote a `Paginated` under the key
     * its Duties screen read with `useInfiniteQuery`, and `data.pages.flatMap` threw on a
     * number. `'history'` cannot collide with a version id, which is an ObjectId.
     *
     * Still UNDER `versions(policyId)`, so a publish invalidating that prefix catches the
     * history as well as the version it just froze.
     */
    history: (policyId: string) => [...queryKeys.policies.versions(policyId), 'history'] as const,
  },
  /**
   * [C1h §1] The system-wide settings store.
   *
   * [amended LIGHT-COLOURFUL] The UI-mode key left this namespace with the Appearance control:
   * the light palette is fixed to colourful, so the settings screen no longer reads it.
   */
  settings: {
    all: ['settings'] as const,
    /**
     * ⚠️ [UX1 §8] IT HAS A READER OUTSIDE THE SETTINGS SCREEN.
     * `useArrangementAuthoringSetting` is read by `RequireDivision`, the sidebar's `+` action
     * and the arrangements list's primary button, so this entry is on the app's hot path
     * rather than on one page.
     */
    arrangementAuthoring: () => [...queryKeys.settings.all, 'arrangement-authoring'] as const,
  },

  /**
   * [ANALYTICS §5] The Usage Analytics page — one report per window.
   *
   * ⚠️ THE WINDOW IS THE WHOLE KEY, because it is the whole request: `GET /analytics?window=` takes
   * nothing else, so two windows are two cache entries and switching back to one already read
   * shows it at once rather than refetching over a blank page.
   */
  analytics: {
    all: ['analytics'] as const,
    views: () => [...queryKeys.analytics.all, 'view'] as const,
    view: (window: AnalyticsWindow) => [...queryKeys.analytics.views(), window] as const,
  },

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [P7C §2] DUTY ANALYTICS — ONE REPORT PER (RANGE, NARROWING), AND ITS DRILL PAGES.
   *
   *  ⚠️ A TOP-LEVEL NAMESPACE, NOT A CHILD OF `dashboard` OR `analytics`. A `dashboard.all`
   *  invalidation (the configurator saves one) must not refetch a report that does not read the tile
   *  layout, and Usage analytics is a different endpoint whose `analytics.all` means its own windows.
   *
   *  ⚠️ THE KEY IS THE REQUEST: `range` and the `divisionId` actually SENT — `null` until the server has
   *  echoed a COMMISSIONERATE layout, so a narrowing that was never sent never names a cache entry.
   *  The drill rows are a SIBLING of `views()`, never under it: a different shape, and a paged list
   *  must not be swept by a refetch of the report.
   *
   *  ⚠️ [amended P7C.1 §4 · §5] AND THE WINDOW AND THE DIVISION READ AS ARE PART OF THE REQUEST. A custom
   *  pair is its own entry, never its equal preset's — the echo differs (`range: null`) — and a division's
   *  page read from the commissionerate (`asDivisionId`) never shares an entry with the caller's own page.
   *  The two arms are exclusive (`never`), so a key with `range` AND the pair does not compile — a plain
   *  union would admit it, because excess properties are checked against the union as a whole.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  dutyAnalytics: {
    all: ['duty-analytics'] as const,
    views: () => [...queryKeys.dutyAnalytics.all, 'view'] as const,
    view: (
      params: { divisionId: string | null; asDivisionId?: string } & (
        | { range: DutyAnalyticsRange; dateFrom?: never; dateTo?: never }
        | { dateFrom: string; dateTo: string; range?: never }
      ),
    ) => [...queryKeys.dutyAnalytics.views(), params] as const,
    rows: (params: object) => [...queryKeys.dutyAnalytics.all, 'rows', params] as const,
  },

  /**
   * [LOCATION-INTEL] Locational Intel — a top-level namespace, Duty analytics' reason: the map is a
   * cross-collection read that belongs to no master, so it nests under nothing an officer mutation
   * invalidates.
   *
   * ⚠️ THE CARD IS ITS OWN ENTRY, PER OFFICER, READ ONLY WHEN A DOT IS CLICKED — the user's rule that the
   * dots carry nobody's details. It is not audited, so it is an ordinary query; nothing prefetches it.
   */
  locationIntel: {
    all: ['location-intel'] as const,
    config: () => [...queryKeys.locationIntel.all, 'config'] as const,
    points: () => [...queryKeys.locationIntel.all, 'points'] as const,
    officer: (officerId: string) => [...queryKeys.locationIntel.all, 'officer', officerId] as const,
  },

  /**
   * [ANALYTICS §8] The Audit log page — one server-paginated list, keyed by its whole parameter set.
   *
   * ⚠️ ONE THING INVALIDATES IT, AND IT IS A READ. The log is written by other screens' mutations and
   * is read, not edited, here; a fresh visit refetches (`staleTime` is the client default), and those
   * mutations do NOT keep this page live. The exception is [ANALYTICS §6]'s officer trail panel on the
   * Usage Analytics page: opening a trail writes `OFFICER_TRAIL_VIEWED` under the viewer's own name,
   * so a cached Audit log would otherwise hide from that same super admin the row they just caused.
   * It invalidates `audit.all` on SETTLE — never on success alone, because the server commits the row
   * before it answers and a failed or lost response may still have written one — and nothing else does.
   */
  audit: {
    all: ['audit'] as const,
    lists: () => [...queryKeys.audit.all, 'list'] as const,
    list: (params: object) => [...queryKeys.audit.lists(), params] as const,
  },

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [C3a §2] THE UPLOADED-ORDER LIBRARY — A **TOP-LEVEL** NAMESPACE, NOT A CHILD OF
   *  `arrangements`.
   *
   *  ⚠️ AN UPLOADED ORDER IS NOT A DESCENDANT OF ANY ARRANGEMENT. Nesting it would make
   *  `queryKeys.arrangements.all` a PREFIX of it, so every arrangement invalidation in the app
   *  would refetch the library too — and, worse, would put it inside the blast radius of the
   *  one key `saveKeyIsolation.test.ts` exists to keep the builder away from.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  uploadedOrders: {
    all: ['uploaded-orders'] as const,
    lists: () => [...queryKeys.uploadedOrders.all, 'list'] as const,
    list: (params: unknown) => [...queryKeys.uploadedOrders.lists(), params] as const,
  },

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [P7B §2-§4] TEMPLATE SETS, THEIR TEMPLATES, DECISIONS, CALENDARS, PLANS AND RUNS — A
   *  **TOP-LEVEL** NAMESPACE, NOT A CHILD OF `arrangements`.
   *
   *  ⚠️ A TEMPLATE IS NOT AN ARRANGEMENT, and nothing that lists arrangements reads one. Nesting it
   *  would put every arrangement invalidation in the app — and there are many, at `arrangements.all`
   *  — into the library's blast radius, refetching a 182-row read for no reason.
   *
   *  ⚠️ EVERYTHING ABOUT ONE SET NESTS UNDER `set(setKey)`, so a decision saved invalidates the set's
   *  readiness, its templates and its decision lists together — one deliberate act, the dashboard
   *  layout's argument. The PLAN and the RUNS are siblings of the sets, never under one: a plan is
   *  re-asked by hand ("nothing is written until Approve"), and a decision saved elsewhere must not
   *  silently re-plan the screen somebody is reading before they approve it.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  templates: {
    all: ['templates'] as const,
    sets: () => [...queryKeys.templates.all, 'set'] as const,
    set: (setKey: string) => [...queryKeys.templates.sets(), setKey] as const,
    list: (setKey: string) => [...queryKeys.templates.set(setKey), 'templates'] as const,
    template: (setKey: string, templateId: string) =>
      [...queryKeys.templates.set(setKey), 'template', templateId] as const,
    decisions: (setKey: string, decisionClass: string) =>
      [...queryKeys.templates.set(setKey), 'decisions', decisionClass] as const,
    calendar: (setKey: string, year: number) => [...queryKeys.templates.set(setKey), 'calendar', year] as const,
    plans: () => [...queryKeys.templates.all, 'plan'] as const,
    plan: (setKey: string, templateIds: readonly string[], year: number) =>
      [...queryKeys.templates.plans(), setKey, year, [...templateIds]] as const,
    runs: () => [...queryKeys.templates.all, 'run'] as const,
    run: (runId: string) => [...queryKeys.templates.runs(), runId] as const,
    /**
     * The type's drafts, read to exhaustion for bulk publish — a sibling, so a run never refetches it mid-way.
     * [DP2026] Keyed by the SET too: two booklets make drafts of one type, and each page offers only its own.
     */
    publishable: (setKey: string, type: string) => [...queryKeys.templates.all, 'publishable', setKey, type] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
